import { axiosInstance } from "@/config/axios-wrapper";
import { V2Response } from "@/types/api";
import { ApplyRequest, Job } from "@/types/job";
import { JobApplicationBasic, JobApplicationStatus } from "@/types/job_application";
import queryString from "query-string";

export const getJobById = async (jobId: string) => {
  const response = await axiosInstance.get<V2Response<Job>>(`/jobs/${jobId}`);
  return response.data.result;
};

export const createJob = async (job) => {
  const response = await axiosInstance.post<
    V2Response<{
      id: string;
      active: boolean;
      alt_id: string;
      benefits: string[];
      address: {
        line1: string;
        line2: string;
        country: string;
        state: string;
        city: string;
        zip_code: string;
      };
      details: string;
      experience_years: { min: number; max: number };
      industry: string;
      organization: { id: string; name: string; logo: string; details: string };
      salary_type: string;
      salary: { min: number; max: number };
      skills: string[];
      title: string;
      type: string;
      video: string;
      work_preference: string;
      created_at: string;
    }>
  >("/jobs", job);
  return response.data.result;
};

export const updateJob = async (jobId: string, job) => {
  const response = await axiosInstance.put<V2Response<Job>>(`/jobs/${jobId}`, job);
  return response.data.result;
};

export const deleteJob = async (jobId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(`/jobs/${jobId}`);
  return response.data.result;
};

export const updateJobVideo = async (jobId: string, path: string) => {
  const response = await axiosInstance.patch<V2Response<any>>(`/jobs/${jobId}/video`, { path });
  return response.data.result;
};

export const updateJobStatus = async (jobId: string, active: boolean) => {
  const response = await axiosInstance.patch<V2Response<any>>(`/jobs/${jobId}/active`, { active });
  return response.data.result;
};

export const deleteJobVideo = async (jobId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(`/jobs/${jobId}/video`);
  return response.data.result;
};

export const archieveJob = async (userId: string, jobId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(`users/${userId}/archived/${jobId}`);
  return response.data.result;
};

export const getArchivedJobs = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}/archived`);
  return response.data.result;
};

export const unArchivedJob = async (userId: string, jobId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/users/${userId}/archived/${jobId}`,
  );
  return response.data.result;
};

export const getJobMatchingPercentageDetails = async (jobseekerId: string, jobIds: string[]) => {
  const payload = {
    job_ids: jobIds,
  };
  const response = await axiosInstance.post<V2Response<any>>(
    `jobseekers/${jobseekerId}/matchingDetails`,
    payload,
  );
  return response.data.result;
};

export const getJobApplyRequests = async (jobId: string) => {
  const response = await axiosInstance.get<V2Response<ApplyRequest[]>>(
    `/jobs/${jobId}/apply-requests`,
  );
  return response.data.result;
};

export const sendApplyRequest = async (jobId: string, jobseeker_id: string) => {
  const payload = { jobseeker_id };
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/apply-requests`,
    payload,
  );
  return response.data.result;
};

export const cancelApplyRequest = async (jobId: string, applyRequestId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/apply-requests/${applyRequestId}/cancel`,
  );
  return response.data.result;
};

export const declineApplyRequest = async (jobId: string, applyRequestId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/apply-requests/${applyRequestId}/decline`,
  );
  return response.data.result;
};

/**
 * Only the bounds that are set, or null when neither is. Zero means "not set",
 * so an open-ended band ("10+ yrs") travels as its min alone and the service
 * applies just that bound.
 */
const toExperienceParam = ({ min, max }: { min: number; max: number }) => {
  const bounds = { ...(min > 0 ? { min } : {}), ...(max > 0 ? { max } : {}) };
  return Object.keys(bounds).length > 0 ? JSON.stringify(bounds) : null;
};

export const getJobApplications = async (
  jobId: string,
  {
    query,
    sort,
    status,
    radius,
    country,
    state,
    city,
    experience_years,
  }: {
    query: string;
    status: JobApplicationStatus[];
    sort: "top_rated" | "new_to_old" | "old_to_new";
    radius: number;
    country: string;
    state: string;
    city: string;
    experience_years: { min: number; max: number };
  },
) => {
  const queryParams = {
    query,
    status,
    sort,
    radius: radius === 0 ? null : radius,
    country,
    state,
    city,
    experience: toExperienceParam(experience_years),
  };

  const queryStringParams = queryString.stringify(queryParams, {
    skipEmptyString: true,
    skipNull: true,
  });

  const response = await axiosInstance.get<V2Response<{ applications: JobApplicationBasic[] }>>(
    `/jobs/${jobId}/applications?${queryStringParams}`,
  );
  return response.data.result;
};

export const deleteJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/applications/${applicationId}/delete`,
  );
  return response.data.result;
};

export const shortlistJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/applications/${applicationId}/shortlist`,
  );
  return response.data.result;
};

export const restoreJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/applications/${applicationId}/restore`,
  );
  return response.data.result;
};

export const hireJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/applications/${applicationId}/hire`,
  );
  return response.data.result;
};
